import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { SUPPORTED_ASSETS } from "@/lib/constants";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import HomePage from "@/pages/HomePage";
import QRScannerPage from "@/pages/QRScannerPage";
import PaymentConfirmPage from "@/pages/PaymentConfirmPage";
import TopUpPage from "@/pages/TopUpPage";
import RubTopUpPage from "@/pages/RubTopUpPage";
import ProcessingTopUpPage from "@/pages/ProcessingTopUpPage";
import SendPage from "@/pages/SendPage";
import SwapPage from "@/pages/SwapPage";
import HistoryPage from "@/pages/HistoryPage";
import ProfilePage from "@/pages/ProfilePage";
import TransactionDetailPage from "@/pages/TransactionDetailPage";
import ServicesPage from "@/pages/ServicesPage";
import CashOutPage from "@/pages/CashOutPage";
import FortuneWheelPage from "@/pages/FortuneWheelPage";
import ReferralPage from "@/pages/ReferralPage";
import GamesPage from "@/pages/GamesPage";
import GameDetailsPage from "@/pages/GameDetailsPage";
import MagicCupPage from "@/pages/MagicCupPage";
import AccountPage from "@/pages/AccountPage";
import CardsPage from "@/pages/CardsPage";
import IssueCardPage from "@/pages/IssueCardPage";
import CardDetailsPage from "@/pages/CardDetailsPage";
import AmlCheckPage from "@/pages/AmlCheckPage";
import AmlHistoryPage from "@/pages/AmlHistoryPage";
import { useTranslation } from "react-i18next";

// Components
import Layout from "@/components/Layout";
import BottomNav from "@/components/BottomNav";
import AmbientField from "@/components/AmbientField";
import SplashScreen from "@/components/SplashScreen";

// Полный такт разряда и длительность ухода. Первое — копия --lp-loop из
// styles/splash.css: разъедутся — экран начнёт обрываться посреди удара.
const SPLASH_BEAT_MS = 2400;
const SPLASH_FADE_MS = 280;
import WebAuthScreen from "@/components/WebAuthScreen";
import PinLockScreen from "@/components/PinLockScreen";
import {
  GRACE_MS,
  cancelUnlockWaiters,
  clearUnlockTicket,
  getUnlockTicket,
  hasTrustedOverlay,
  onLockRequired,
  releaseUnlockWaiters,
  requestLock,
  unlockReasonForUrl,
  waitForUnlock,
} from "@/lib/appLock";
import AccountBlockedScreen from "@/components/AccountBlockedScreen";
import {
  getAccountBlock,
  isAccountBlockedError,
  notifyAccountBlocked,
  onAccountBlocked,
} from "@/lib/accountBlock";

const resolveBackendUrl = () => {
  const override = process.env.REACT_APP_BACKEND_URL;
  if (override) return override;

  if (typeof window !== "undefined" && window.location?.hostname) {
    const { hostname, origin } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `http://${hostname}:8000`;
    }
    // In production we keep API on the same origin and proxy /api via nginx.
    return origin;
  }

  return process.env.NODE_ENV === "development"
    ? "http://localhost:8000"
    : "https://lightpaytest.duckdns.org";
};

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;
const DISPLAY_FIAT_CURRENCY = "USD";
// Валюты отображения. Курс к USD берётся из /config (usd_<code>_rate).
const SUPPORTED_DISPLAY_CURRENCIES = ["USD", "RUB"];

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

let sessionAuthToken = "";
const WEB_ACTIVITY_PING_MIN_INTERVAL_MS = 60 * 1000;
const WEB_TOKEN_REFRESH_MIN_INTERVAL_MS = 15 * 60 * 1000;

const safeGetPersistedToken = () => {
  if (typeof window === "undefined") return "";
  const stores = [window.localStorage, window.sessionStorage];
  try {
    for (const storage of stores) {
      const value = storage?.getItem?.("auth_token") || "";
      if (value) return value;
    }
    return "";
  } catch (_) {
    return "";
  }
};

const safeSetPersistedToken = (token) => {
  if (typeof window === "undefined") return;
  const normalized = (token || "").trim();
  try {
    if (normalized) {
      window.localStorage?.setItem?.("auth_token", normalized);
      window.sessionStorage?.setItem?.("auth_token", normalized);
    } else {
      window.localStorage?.removeItem?.("auth_token");
      window.sessionStorage?.removeItem?.("auth_token");
    }
  } catch (_) {
    // Some Telegram webviews can block storage; keep in-memory token as fallback.
  }
};

const applyAuthHeader = (token) => {
  if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete api.defaults.headers.common.Authorization;
};

const notifyAuthTokenUpdated = (token) => {
  if (typeof window === "undefined") return;
  const normalized = (token || "").trim();
  try {
    window.dispatchEvent(new CustomEvent("lightpay:auth-token-updated", {
      detail: {
        hasToken: Boolean(normalized),
      },
    }));
  } catch (_) {}
};

const setSessionToken = (token) => {
  const prev = String(sessionAuthToken || "").trim();
  const normalized = (token || "").trim();
  sessionAuthToken = normalized;
  applyAuthHeader(normalized);
  safeSetPersistedToken(normalized);
  if (prev !== normalized) {
    notifyAuthTokenUpdated(normalized);
  }
};

const getAuthToken = () => {
  return sessionAuthToken || safeGetPersistedToken();
};

const normalizeDisplayCurrency = (value) => {
  const code = String(value || "").trim().toUpperCase();
  return SUPPORTED_DISPLAY_CURRENCIES.includes(code) ? code : DISPLAY_FIAT_CURRENCY;
};

const isInitDataAuthError = (backendDetail) => {
  const low = String(backendDetail || "").trim().toLowerCase();
  if (!low) return false;
  return low.includes("telegram auth failed");
};

const buildAuthErrorMessage = (t, backendDetail) => {
  if (isInitDataAuthError(backendDetail)) {
    return t(
      "common.error_auth_initdata_relogin",
      {
        defaultValue: "Ошибка аутентификации (initData) Закройте мини-апп и войдите заново",
      },
    );
  }
  return typeof backendDetail === "string" && backendDetail
    ? `${t("common.error_auth")}: ${backendDetail}`
    : t("common.error_auth");
};

const notifyWebAuthExpired = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("lightpay:web-auth-expired"));
  } catch (_) {}
};

let telegramReauthPromise = null;
let webReauthPromise = null;
let lastWebRefreshAt = 0;

const refreshTelegramSessionToken = async () => {
  if (telegramReauthPromise) return telegramReauthPromise;

  telegramReauthPromise = (async () => {
    if (typeof window === "undefined") return "";
    const initData = (window.Telegram?.WebApp?.initData || "").trim();
    if (!initData) return "";

    let response;
    try {
      response = await axios.post(
        `${API}/telegram/auth`,
        { initData },
        {
          headers: {
            "Content-Type": "application/json",
            "X-TG-INIT-DATA": initData,
          },
          timeout: 12000,
        }
      );
    } catch (error) {
      // Этот запрос идёт мимо api-интерцептора (он сам его и чинит), поэтому
      // блокировку здесь разбираем отдельно: иначе заблокированный получил бы
      // молчаливый отказ реаутентификации вместо окна.
      if (isAccountBlockedError(error)) notifyAccountBlocked(error?.response?.data?.detail);
      throw error;
    }
    const token = String(response?.data?.token || "").trim();
    if (token) setSessionToken(token);
    return token;
  })();

  try {
    return await telegramReauthPromise;
  } finally {
    telegramReauthPromise = null;
  }
};

const refreshWebSessionToken = async ({ force = false } = {}) => {
  if (webReauthPromise) return webReauthPromise;

  const token = getAuthToken();
  if (!token) return "";

  const now = Date.now();
  if (!force && (now - Number(lastWebRefreshAt || 0)) < WEB_TOKEN_REFRESH_MIN_INTERVAL_MS) {
    return token;
  }

  webReauthPromise = (async () => {
    const response = await axios.post(
      `${API}/auth/refresh`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        timeout: 12000,
      }
    );
    const nextToken = String(response?.data?.token || "").trim();
    if (nextToken) {
      setSessionToken(nextToken);
      lastWebRefreshAt = Date.now();
    }
    return nextToken;
  })();

  try {
    return await webReauthPromise;
  } finally {
    webReauthPromise = null;
  }
};

const TELEGRAM_OAUTH_QUERY_KEYS = ["tg_code", "tg_token", "tg_error", "tg_linked"];

const readTelegramOAuthQuery = () => {
  if (typeof window === "undefined") {
    return { tgCode: "", tgError: "", tgLinked: false };
  }
  const params = new URLSearchParams(window.location.search || "");
  const tgCode = (params.get("tg_code") || "").trim();
  const tgError = (params.get("tg_error") || "").trim();
  const tgLinked = params.get("tg_linked") === "1";
  return { tgCode, tgError, tgLinked };
};

const clearTelegramOAuthQuery = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  TELEGRAM_OAUTH_QUERY_KEYS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }
};

// Add auth interceptor
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const initData = window.Telegram?.WebApp
    ? readTelegramInitDataStrict()
    : readTelegramInitData();
  if (initData) config.headers["X-TG-INIT-DATA"] = initData;

  const explicitAuthHeader =
    config?.headers?.Authorization ||
    config?.headers?.authorization ||
    "";
  if (typeof explicitAuthHeader === "string" && explicitAuthHeader.trim()) {
    return config;
  }

  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  else if (config?.headers?.Authorization) delete config.headers.Authorization;

  // Step-up для денежных операций: тикет живёт только в памяти.
  const unlockTicket = getUnlockTicket();
  if (unlockTicket && !config.headers["X-Unlock-Ticket"]) {
    config.headers["X-Unlock-Ticket"] = unlockTicket;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = Number(error?.response?.status || 0);
    const originalRequest = error?.config || {};

    // 428 unlock_required — ДО обработки 401/403 (те уходят в молчаливую
    // реаутентификацию). Показываем экран кода и повторяем запрос один раз.
    if (status === 428) {
      const detail = error?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? String(detail.code || "") : "";
      if (code === "unlock_required" && !originalRequest?._retryUnlock) {
        clearUnlockTicket();
        requestLock(unlockReasonForUrl(originalRequest?.url));
        // Отказ от ввода — не ошибка сети: отдаём исходный 428 с меткой, по
        // которой страница отличит «человек передумал» от «не получилось».
        if (!(await waitForUnlock())) {
          error.__unlockCancelled = true;
          return Promise.reject(error);
        }
        originalRequest._retryUnlock = true;
        const freshTicket = getUnlockTicket();
        originalRequest.headers = { ...(originalRequest.headers || {}) };
        if (freshTicket) originalRequest.headers["X-Unlock-Ticket"] = freshTicket;
        else delete originalRequest.headers["X-Unlock-Ticket"];
        return api.request(originalRequest);
      }
      return Promise.reject(error);
    }

    // Блокировка аккаунта — ДО общей обработки 403. Иначе запрос ушёл бы в
    // молчаливую переавторизацию: она получит тот же отказ, и человек увидит
    // бесконечный спиннер вместо объяснения, что произошло.
    if (isAccountBlockedError(error)) {
      notifyAccountBlocked(error?.response?.data?.detail);
      return Promise.reject(error);
    }

    if (!(status === 401 || status === 403)) {
      return Promise.reject(error);
    }
    if (originalRequest?._retryTelegramReauth) {
      return Promise.reject(error);
    }

    const reqUrl = String(originalRequest?.url || "");
    if (reqUrl.includes("/telegram/auth") || reqUrl.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    const initData = (window.Telegram?.WebApp?.initData || "").trim();
    if (initData) {
      try {
        const freshToken = await refreshTelegramSessionToken();
        if (!freshToken) {
          return Promise.reject(error);
        }
        originalRequest._retryTelegramReauth = true;
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${freshToken}`,
          "X-TG-INIT-DATA": initData,
        };
        return api.request(originalRequest);
      } catch (_) {
        return Promise.reject(error);
      }
    }

    try {
      const freshToken = await refreshWebSessionToken({ force: true });
      if (!freshToken) {
        setSessionToken("");
        notifyWebAuthExpired();
        return Promise.reject(error);
      }
      originalRequest._retryTelegramReauth = true;
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${freshToken}`,
      };
      return api.request(originalRequest);
    } catch (_) {
      setSessionToken("");
      notifyWebAuthExpired();
      return Promise.reject(error);
    }
  }
);

// То же самое, но на дефолтном инстансе axios. QRScannerPage — единственная
// страница, которая ходит не через `api`, а сырым axios с самособранными
// заголовками, и /qr-intent/{id}/pay как раз денежный: без этого он ловит 428
// unlock_required, до экрана кода дело не доходит и юзер видит «Ошибка
// обработки QR». Интерцепторы дефолтного axios на инстансы из axios.create не
// распространяются, так что задвоения с блоком выше нет.
axios.interceptors.request.use((config) => {
  const unlockTicket = getUnlockTicket();
  if (unlockTicket && !config.headers?.["X-Unlock-Ticket"]) {
    config.headers = { ...(config.headers || {}), "X-Unlock-Ticket": unlockTicket };
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (Number(error?.response?.status || 0) !== 428) return Promise.reject(error);
    const originalRequest = error?.config || {};
    const detail = error?.response?.data?.detail;
    const code = detail && typeof detail === "object" ? String(detail.code || "") : "";
    if (code !== "unlock_required" || originalRequest?._retryUnlock) {
      return Promise.reject(error);
    }
    clearUnlockTicket();
    requestLock(unlockReasonForUrl(originalRequest?.url));
    if (!(await waitForUnlock())) {
      error.__unlockCancelled = true;
      return Promise.reject(error);
    }
    originalRequest._retryUnlock = true;
    const freshTicket = getUnlockTicket();
    originalRequest.headers = { ...(originalRequest.headers || {}) };
    if (freshTicket) originalRequest.headers["X-Unlock-Ticket"] = freshTicket;
    else delete originalRequest.headers["X-Unlock-Ticket"];
    return axios.request(originalRequest);
  }
);


// Context for app state
const AppContext = createContext();

export const useApp = () => useContext(AppContext);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractTgWebAppDataFromUrl = () => {
  if (typeof window === "undefined") return "";

  const readRawParam = (raw, key) => {
    const input = String(raw || "").replace(/^[?#]/, "");
    if (!input) return "";
    const pairs = input.split("&");
    for (const pair of pairs) {
      if (!pair) continue;
      const idx = pair.indexOf("=");
      const k = idx >= 0 ? pair.slice(0, idx) : pair;
      if (k !== key) continue;
      return idx >= 0 ? pair.slice(idx + 1) : "";
    }
    return "";
  };

  const decodeOnce = (raw) => {
    const value = String(raw || "");
    if (!value) return "";
    try {
      return decodeURIComponent(value);
    } catch (_) {
      return value;
    }
  };

  // Telegram Web can pass initData in URL params/hash when WebApp API is not ready yet.
  const encodedFromSearch = readRawParam(window.location.search || "", "tgWebAppData");
  if (encodedFromSearch) return decodeOnce(encodedFromSearch);

  const rawHash = (window.location.hash || "").replace(/^#/, "");
  const encodedFromHash = readRawParam(rawHash, "tgWebAppData");
  if (encodedFromHash) return decodeOnce(encodedFromHash);

  return "";
};

const readTelegramInitData = () => {
  const initData = window.Telegram?.WebApp?.initData;
  if (typeof initData === "string" && initData.trim()) return initData.trim();

  const fromUrl = extractTgWebAppDataFromUrl();
  if (typeof fromUrl === "string" && fromUrl.trim()) return fromUrl.trim();

  return "";
};

const readTelegramInitDataStrict = () => {
  const initData = window.Telegram?.WebApp?.initData;
  if (typeof initData === "string" && initData.trim()) return initData.trim();
  return "";
};

const waitForTelegramInitData = async ({ timeoutMs = 6000, pollMs = 100, strict = false } = {}) => {
  const reader = strict ? readTelegramInitDataStrict : readTelegramInitData;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const initData = reader();
    if (initData) return initData;
    await sleep(pollMs);
  }
  return reader();
};

const isTelegramWebAppRuntime = () => {
  if (typeof window === "undefined") return false;
  const tg = window.Telegram?.WebApp;
  const initData = typeof tg?.initData === "string" ? tg.initData.trim() : "";
  if (initData) return true;
  if (readTelegramInitData()) return true;

  const markers = `${window.location.search || ""}&${window.location.hash || ""}`;
  if (/tgWebAppData=|tgWebAppVersion=|tgWebAppPlatform=|tgWebAppThemeParams=/.test(markers)) {
    return true;
  }

  const ua = window.navigator?.userAgent || "";
  if (/Telegram/i.test(ua)) return true;
  return false;
};

const isLandscapeOnMobile = () => {
  if (typeof window === "undefined") return false;

  const orientationType = window.screen?.orientation?.type;
  const matchMediaLandscape = window.matchMedia?.("(orientation: landscape)")?.matches;
  const legacyOrientation = typeof window.orientation === "number"
    ? Math.abs(Number(window.orientation)) === 90
    : null;

  let isLandscape = false;
  if (typeof orientationType === "string") {
    isLandscape = orientationType.startsWith("landscape");
  } else if (legacyOrientation !== null) {
    isLandscape = legacyOrientation;
  } else if (typeof matchMediaLandscape === "boolean") {
    isLandscape = matchMediaLandscape;
  } else {
    isLandscape = window.innerWidth > window.innerHeight;
  }

  const hasCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const isMobileUa = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || "");
  return isLandscape && (hasCoarsePointer || isMobileUa);
};

const lockPortraitOrientation = async (tg) => {
  if (typeof window === "undefined") return;

  // Telegram WebApp API (if supported by client version)
  try {
    tg?.lockOrientation?.("portrait");
  } catch (_) {
    // ignore unsupported client versions
  }

  // Browser / WebView API fallback
  const orientation = window.screen?.orientation;
  if (orientation?.lock) {
    try {
      await orientation.lock("portrait-primary");
      return;
    } catch (_) {
      try {
        await orientation.lock("portrait");
        return;
      } catch (_) {
        // ignore if not allowed by the platform
      }
    }
  }

  // Legacy fallback for old browsers/webviews
  const legacyLock =
    window.screen?.lockOrientation ||
    window.screen?.mozLockOrientation ||
    window.screen?.msLockOrientation;
  if (typeof legacyLock === "function") {
    try {
      legacyLock.call(window.screen, "portrait");
    } catch (_) {
      // ignore unsupported platform behavior
    }
  }
};

// Telegram WebApp init helper.
const initTelegramWebApp = () => {
  if (window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp;
    tg.ready();
    tg.expand();
    return tg;
  }

  // Keep mock only in local development.
  if (process.env.NODE_ENV === "development") {
    return {
      initData: "user=%7B%22id%22%3A%22demo_123%22%2C%22first_name%22%3A%22Demo%22%2C%22last_name%22%3A%22User%22%2C%22username%22%3A%22demo_user%22%7D",
      initDataUnsafe: {
        user: {
          id: "demo_123",
          first_name: "Demo",
          last_name: "User",
          username: "demo_user"
        }
      },
      themeParams: {
        bg_color: "#ffffff",
        text_color: "#000000",
        hint_color: "#999999",
        link_color: "#2563eb",
        button_color: "#2563eb",
        button_text_color: "#ffffff"
      },
      colorScheme: "light",
      MainButton: {
        text: "",
        isVisible: false,
        show: function () { this.isVisible = true; },
        hide: function () { this.isVisible = false; },
        setText: function (text) { this.text = text; },
        onClick: function () { },
        offClick: function () { }
      },
      BackButton: {
        isVisible: false,
        show: function () { this.isVisible = true; },
        hide: function () { this.isVisible = false; },
        onClick: function () { },
        offClick: function () { }
      },
      HapticFeedback: {
        impactOccurred: (style) => console.log('Haptic:', style),
        notificationOccurred: (type) => console.log('Notification:', type),
        selectionChanged: () => console.log('Selection changed')
      },
      ready: () => { },
      expand: () => { },
      close: () => { },
      onEvent: () => { },
      offEvent: () => { },
      isVersionAtLeast: () => false,
      platform: "unknown",
      version: "6.0"
    };
  }

  // In production, do not use fake initData. This allows
  // the auth flow to show the proper "open in Telegram" state
  // and prevents invalid-signature auth failures.
  return {
    initData: "",
    initDataUnsafe: {},
    themeParams: {
      bg_color: "#ffffff",
      text_color: "#000000",
      hint_color: "#999999",
      link_color: "#2563eb",
      button_color: "#2563eb",
      button_text_color: "#ffffff"
    },
    colorScheme: "light",
    MainButton: {
      text: "",
      isVisible: false,
      show: function () { this.isVisible = true; },
      hide: function () { this.isVisible = false; },
      setText: function (text) { this.text = text; },
      onClick: function () { },
      offClick: function () { }
    },
    BackButton: {
      isVisible: false,
      show: function () { this.isVisible = true; },
      hide: function () { this.isVisible = false; },
      onClick: function () { },
      offClick: function () { }
    },
    HapticFeedback: {
      impactOccurred: (style) => console.log('Haptic:', style),
      notificationOccurred: (type) => console.log('Notification:', type),
      selectionChanged: () => console.log('Selection changed')
    },
    ready: () => { },
    expand: () => { },
    close: () => { },
    onEvent: () => { },
    offEvent: () => { },
    isVersionAtLeast: () => false,
    platform: "unknown",
    version: "6.0"
  };
};

const resolvePreferredDarkMode = (tg) => {
  let savedTheme = "";
  try {
    savedTheme = localStorage.getItem('theme') || "";
  } catch (_) {
    savedTheme = "";
  }
  if (savedTheme === 'dark') return true;
  if (savedTheme === 'light') return false;

  if (isTelegramWebAppRuntime()) {
    return tg?.colorScheme === 'dark';
  }
  // Web default for first-time users.
  return true;
};

function AppContent() {
  const { t } = useTranslation();
  const isTelegramWebApp = isTelegramWebAppRuntime();
  const initialOauthQuery = readTelegramOAuthQuery();
  const initialToken = getAuthToken();
  const shouldShowWebAuthImmediately =
    !isTelegramWebApp &&
    !initialOauthQuery.tgCode &&
    !initialToken;

  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [balances, setBalances] = useState(() =>
    SUPPORTED_ASSETS.map(asset => ({
      ...asset,
      amount: 0,
      usd_value: null,
      usd_rate: null
    }))
  );
  const [market, setMarket] = useState({ prices: {}, changes: {}, ts: null, source: null });
  const [config, setConfig] = useState(null);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(!shouldShowWebAuthImmediately);
  // Экран загрузки держим ПОЛНЫЙ такт разряда, даже если данные пришли
  // раньше: он показывается ради самой анимации, а обрыв на середине съедает
  // то, ради чего его показывают. Такт — одна завершённая фраза: заряд
  // стягивается, бьёт, расходится волной, гаснет, и остаётся тишина. Уходит
  // экран как раз на этой тишине, поэтому подмена не выглядит рывком.
  //
  // SPLASH_BEAT_MS обязан совпадать с --lp-loop из styles/splash.css.
  const [splashVisible, setSplashVisible] = useState(!shouldShowWebAuthImmediately);
  const [splashLeaving, setSplashLeaving] = useState(false);
  const splashShownAt = useRef(Date.now());

  // Загрузка может начаться повторно — например, после входа через веб.
  // Тогда такт отсчитывается заново, а не от первого показа.
  useEffect(() => {
    if (!loading) return;
    splashShownAt.current = Date.now();
    setSplashLeaving(false);
    setSplashVisible(true);
  }, [loading]);

  useEffect(() => {
    if (loading || !splashVisible) return undefined;
    const left = Math.max(0, SPLASH_BEAT_MS - (Date.now() - splashShownAt.current));
    let fade = null;
    const hold = setTimeout(() => {
      setSplashLeaving(true);
      fade = setTimeout(() => setSplashVisible(false), SPLASH_FADE_MS);
    }, left);
    return () => {
      clearTimeout(hold);
      if (fade) clearTimeout(fade);
    };
  }, [loading, splashVisible]);
  const [hideBalance, setHideBalance] = useState(false);
  const [preferredCurrency, setPreferredCurrency] = useState(DISPLAY_FIAT_CURRENCY);
  const [showFiat, setShowFiat] = useState(true);
  const [telegram, setTelegram] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [webAuthRequired, setWebAuthRequired] = useState(shouldShowWebAuthImmediately);
  // Блокировка могла прийти до монтирования — начальное значение берём из шины.
  const [accountBlock, setAccountBlock] = useState(getAccountBlock);
  const [isLandscapeBlocked, setIsLandscapeBlocked] = useState(() => isLandscapeOnMobile());
  const [sseReconnectTick, setSseReconnectTick] = useState(0);
  // Код-пароль: pinLocked — показать замок; hasUnlockedThisSession — уже вводил
  // код в этой сессии (перезапирание рисуем оверлеем, а не заменой дерева).
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pinLocked, setPinLocked] = useState(false);
  const [hasUnlockedThisSession, setHasUnlockedThisSession] = useState(false);
  // Шаг-ап: подтверждение конкретного действия внутри разблокированного
  // приложения. Отдельное состояние, а не флаг у pinLocked, — намеренно:
  // pinLocked означает «кошелёк заперт» и завязан на возврат из фона, ссылки
  // на маршруты и холодный старт. Реквизиты карты кошелёк не запирают.
  const [stepUp, setStepUp] = useState(null);   // null | { reason }
  const pinEnabledRef = useRef(false);
  const pinLockedRef = useRef(false);
  const stepUpRef = useRef(null);
  const hiddenAtRef = useRef(null);
  const locationRef = useRef("/");
  const backHandlerRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const sseRef = useRef(null);
  const lastSeenBalanceTxIdRef = useRef(0);
  const webActivityPingInFlightRef = useRef(false);
  const lastWebActivityPingAtRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();

  const authenticateTelegram = async (initData, attempts = 3) => {
    let lastError = null;
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await api.post('/telegram/auth', { initData });
      } catch (err) {
        lastError = err;
        const status = Number(err?.response?.status || 0);
        const retryable = !status || status === 408 || status === 429 || status >= 500;
        if (!retryable || i >= attempts - 1) break;
        await sleep(350 * (i + 1));
      }
    }
    throw lastError || new Error("auth_failed");
  };

  const applyAuthenticatedSession = async (token, userData) => {
    setSessionToken(token);
    setUser(userData);
    setAuthError(null);
    setWebAuthRequired(false);
    // Пин-состояние выставляем синхронно, ДО fetchWalletData — иначе на
    // холодном старте мигнёт баланс до появления экрана кода.
    const pinOn = Boolean(userData?.settings?.pin_enabled);
    setPinEnabled(pinOn);
    pinEnabledRef.current = pinOn;
    if (pinOn && !hasUnlockedThisSession && !getUnlockTicket()) {
      setPinLocked(true);
      pinLockedRef.current = true;
    }
    const normalizedCurrency = normalizeDisplayCurrency(userData?.settings?.fiat_currency);
    setPreferredCurrency(normalizedCurrency);
    localStorage.setItem("fiatCurrency", normalizedCurrency);
    if (!isTelegramWebApp) {
      await pingWebSessionActivity(true);
    }
    await fetchWalletData(userData?.id);
  };

  const forceWebSessionTimeoutLogout = () => {
    setSessionToken("");
    setUser(null);
    setAuthError(null);
    setWebAuthRequired(true);
    setLoading(false);
    setWallet(null);
    try {
      toast.error(t("common.session_expired", { defaultValue: "Session expired due to inactivity" }));
    } catch (_) {}
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (isTelegramWebApp) return;
    if (typeof window === "undefined") return;

    const onExpired = () => {
      forceWebSessionTimeoutLogout();
    };
    window.addEventListener("lightpay:web-auth-expired", onExpired);
    return () => {
      window.removeEventListener("lightpay:web-auth-expired", onExpired);
    };
  }, [isTelegramWebApp]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onAuthTokenUpdated = (event) => {
      const hasToken = Boolean(event?.detail?.hasToken);
      if (!hasToken) return;
      setSseReconnectTick((prev) => prev + 1);
    };
    window.addEventListener("lightpay:auth-token-updated", onAuthTokenUpdated);
    return () => {
      window.removeEventListener("lightpay:auth-token-updated", onAuthTokenUpdated);
    };
  }, []);

  const pingWebSessionActivity = async (force = false) => {
    if (isTelegramWebApp) return;
    const token = getAuthToken();
    if (!token) return;

    const now = Date.now();
    if (!force && (now - Number(lastWebActivityPingAtRef.current || 0)) < WEB_ACTIVITY_PING_MIN_INTERVAL_MS) {
      return;
    }
    if (webActivityPingInFlightRef.current) return;

    webActivityPingInFlightRef.current = true;
    try {
      await api.post("/auth/activity");
      lastWebActivityPingAtRef.current = now;
    } catch (e) {
      const status = Number(e?.response?.status || 0);
      if (status === 401 || status === 403) {
        forceWebSessionTimeoutLogout();
      }
    } finally {
      webActivityPingInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (isTelegramWebApp) return;
    if (typeof window === "undefined") return;

    const activityEvents = ["mousedown", "keydown", "touchstart", "scroll", "pointerdown"];
    const onActivity = () => {
      void pingWebSessionActivity(false);
      void refreshWebSessionToken({ force: false });
    };

    void pingWebSessionActivity(true);
    void refreshWebSessionToken({ force: true });
    activityEvents.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, onActivity));
    };
  }, [isTelegramWebApp]);

  // Фактический цвет фона приложения в hex — им красится обвязка Telegram
  // (шапка, фон, нижняя полоса), чтобы между нативной рамкой и вебвью не
  // было шва. rgba приходит от getComputedStyle всегда, альфа отбрасывается:
  // фон корня непрозрачен.
  const readRootBackgroundHex = () => {
    if (typeof window === "undefined" || typeof document === "undefined") return "";
    try {
      const raw = window.getComputedStyle(document.documentElement).backgroundColor;
      const m = String(raw).match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
      if (!m) return "";
      const parts = m.slice(1, 4).map(Number);
      if (!parts.every((v) => Number.isFinite(v) && v >= 0 && v <= 255)) return "";
      return `#${parts.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
    } catch (_) {
      return "";
    }
  };

  const resolveTelegramSurface = (isDark, pathname = location.pathname) => {
    const route = String(pathname || "");
    if (route.startsWith("/scan")) return null;


    // Раньше здесь стояли зашитые #101728 / #ffffff. Токен фона с тех пор
    // сменился дважды, и каждый раз между обвязкой Telegram и экраном
    // появлялся видимый шов. Берём фактический цвет корня — кадр ожидания
    // ниже как раз для того и был.
    const bgColor = readRootBackgroundHex() || (isDark ? "#0a0e1a" : "#f4f7fa");
    return {
      bgColor,
      bottomBarColor: bgColor,
    };
  };

  // Sync Telegram shell colors. QR scanner manages its own full-screen colors.
  const syncTelegramTheme = (isDark, options = {}) => {
    if (!telegram) return;
    const surface = resolveTelegramSurface(isDark, options?.pathname);
    if (!surface) return;

    const apply = () => {
      try {
        const { bgColor, bottomBarColor } = surface;

        if (telegram.setHeaderColor) {
          telegram.setHeaderColor(bgColor);
        }
        if (telegram.setBackgroundColor) {
          telegram.setBackgroundColor(bgColor);
        }
        if (telegram.setBottomBarColor) {
          telegram.setBottomBarColor(bottomBarColor);
        }
      } catch (e) {
        console.warn('Telegram theme sync failed:', e);
      }
    };

    // Wait a frame so CSS theme changes are reflected in computed styles.
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(apply);
    } else {
      apply();
    }
  };

  useEffect(() => {
    const tg = initTelegramWebApp();
    setTelegram(tg);
    lockPortraitOrientation(tg);

    // Apply theme: keep saved preference; for first-time web users default to dark.
    const shouldBeDark = resolvePreferredDarkMode(tg);

    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!webAuthRequired) return;

    const hadDarkClass = document.documentElement.classList.contains("dark");
    document.documentElement.classList.add("dark");

    return () => {
      if (!hadDarkClass) {
        document.documentElement.classList.remove("dark");
      }
    };
  }, [webAuthRequired]);

  // Полноэкранный режим.
  //
  // Без него Telegram рисует поверх приложения свою шапку с именем бота и
  // подписью «мини-приложение» — то самое, из-за чего продукт читается как
  // страница внутри мессенджера, а не как приложение.
  //
  // В полноэкранном режиме шапка не исчезает, а становится ПРОЗРАЧНОЙ:
  // круглые «закрыть» и «⋯» продолжают висеть в правом верхнем углу поверх
  // контента. Поэтому мало попросить полный экран — надо ещё освободить под
  // них место, и его размер сообщает сам Telegram через contentSafeAreaInset.
  //
  // Доступно с Bot API 8.0. На клиентах старее просьба молча провалится, и
  // приложение должно остаться работоспособным — отсюда и проверка версии,
  // и обработчик fullscreenFailed.
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return undefined;

    const root = document.documentElement;

    const applyState = () => {
      const on = Boolean(tg.isFullscreen);
      root.classList.toggle("tg-fullscreen", on);
    };

    // Отступы приезжают отдельными событиями и не всегда до первого кадра.
    // Держим их в CSS-переменных сами: у части клиентов Telegram выставляет
    // свои --tg-* с задержкой, и без подстраховки шапка приложения на один
    // кадр уезжает под чёлку.
    const applyInsets = () => {
      const safe = tg.safeAreaInset || {};
      const content = tg.contentSafeAreaInset || {};
      const px = (v) => `${Math.max(0, Number(v) || 0)}px`;
      root.style.setProperty("--lp-safe-top", px(safe.top));
      root.style.setProperty("--lp-safe-bottom", px(safe.bottom));
      root.style.setProperty("--lp-content-top", px(content.top));
    };

    const onChanged = () => { applyState(); applyInsets(); };
    const onFailed = () => { applyState(); };

    tg.onEvent?.("fullscreenChanged", onChanged);
    tg.onEvent?.("fullscreenFailed", onFailed);
    tg.onEvent?.("safeAreaChanged", applyInsets);
    tg.onEvent?.("contentSafeAreaChanged", applyInsets);

    applyState();
    applyInsets();

    if (tg.isVersionAtLeast?.("8.0") && !tg.isFullscreen) {
      try {
        tg.requestFullscreen?.();
      } catch (_) {
        // Клиент не умеет — остаёмся в обычном режиме, это не ошибка.
      }
    }

    // Без этого свайп вниз по длинному списку закрывает приложение вместо
    // прокрутки: Telegram считает движение попыткой смахнуть шторку. На
    // экране с историей операций и списком активов это происходит постоянно
    // и читается как «приложение само вылетает». Bot API 7.7+.
    try {
      tg.disableVerticalSwipes?.();
    } catch (_) {
      /* необязательная возможность */
    }

    return () => {
      tg.offEvent?.("fullscreenChanged", onChanged);
      tg.offEvent?.("fullscreenFailed", onFailed);
      tg.offEvent?.("safeAreaChanged", applyInsets);
      tg.offEvent?.("contentSafeAreaChanged", applyInsets);
    };
  }, []);

  useEffect(() => {
    const syncOrientationState = () => {
      setIsLandscapeBlocked((prev) => {
        const next = isLandscapeOnMobile();
        return prev === next ? prev : next;
      });
    };

    const handleOrientationChange = () => {
      syncOrientationState();
      const tg = window.Telegram?.WebApp;
      lockPortraitOrientation(tg);
    };

    const tg = window.Telegram?.WebApp;
    const orientationMedia = window.matchMedia?.("(orientation: landscape)");
    const handleMediaChange = () => syncOrientationState();
    const handleViewportChanged = () => handleOrientationChange();

    syncOrientationState();
    window.addEventListener("resize", syncOrientationState);
    window.addEventListener("orientationchange", handleOrientationChange);
    window.visualViewport?.addEventListener("resize", syncOrientationState);

    if (orientationMedia?.addEventListener) {
      orientationMedia.addEventListener("change", handleMediaChange);
    } else if (orientationMedia?.addListener) {
      orientationMedia.addListener(handleMediaChange);
    }

    tg?.onEvent?.("viewportChanged", handleViewportChanged);

    // Telegram webview can skip orientation/resize events. Polling keeps state in sync.
    const pollId = window.setInterval(syncOrientationState, 500);

    return () => {
      window.removeEventListener("resize", syncOrientationState);
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.visualViewport?.removeEventListener("resize", syncOrientationState);
      window.clearInterval(pollId);

      if (orientationMedia?.removeEventListener) {
        orientationMedia.removeEventListener("change", handleMediaChange);
      } else if (orientationMedia?.removeListener) {
        orientationMedia.removeListener(handleMediaChange);
      }

      tg?.offEvent?.("viewportChanged", handleViewportChanged);
    };
  }, []);

  useEffect(() => {
    if (!telegram) return undefined;
    if (location.pathname.startsWith("/scan")) return undefined;

    const shouldBeDark = resolvePreferredDarkMode(telegram);
    syncTelegramTheme(shouldBeDark, { pathname: location.pathname });

    const timerId = window.setTimeout(() => {
      syncTelegramTheme(shouldBeDark, { pathname: location.pathname });
    }, 120);

    return () => window.clearTimeout(timerId);
  }, [telegram, location.pathname]);

  // Control Telegram native Back button: show on inner pages, hide on root.
  useEffect(() => {
    if (!telegram?.BackButton) return;

    const onBack = () => {
      if (backHandlerRef.current) {
        backHandlerRef.current();
        return;
      }
      navigate(-1);
    };

    if (location.pathname === "/") {
      telegram.BackButton.hide();
    } else {
      telegram.BackButton.show();
    }

    telegram.BackButton.onClick(onBack);
    return () => telegram.BackButton.offClick(onBack);
  }, [telegram, location.pathname, navigate]);

  const setBackHandler = (handler) => {
    backHandlerRef.current = handler;
  };

  const clearBackHandler = () => {
    backHandlerRef.current = null;
  };

  const updatePreferredCurrency = async (currency) => {
    const normalized = normalizeDisplayCurrency(currency);
    setPreferredCurrency(normalized);
    localStorage.setItem('fiatCurrency', normalized);
    try {
      await api.put('/user/settings', { fiat_currency: normalized });
    } catch (err) {
      console.error('Failed to update currency on backend:', err);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await api.get('/config');
      setConfig(res.data);
    } catch (e) {
      console.error('Error fetching config:', e);
    }
  };

  const formatToastAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "0";
    const decimals = num < 1 ? 6 : 4;
    return num.toFixed(decimals).replace(/\.?0+$/, "");
  };

  const emitTopupCreditToasts = async () => {
    try {
      const res = await api.get("/transactions", {
        params: { limit: 100, offset: 0 },
      });
      const rows = Array.isArray(res?.data?.transactions) ? res.data.transactions : [];
      if (!rows.length) return;

      let maxTxId = 0;
      for (const tx of rows) {
        const txId = Number(tx?.transaction_id || 0);
        if (Number.isFinite(txId) && txId > maxTxId) maxTxId = txId;
      }
      if (maxTxId <= 0) return;

      const prevSeen = Number(lastSeenBalanceTxIdRef.current || 0);
      if (!Number.isFinite(prevSeen) || prevSeen <= 0) {
        // Bootstrap on first successful fetch: do not replay historical topups.
        lastSeenBalanceTxIdRef.current = maxTxId;
        return;
      }

      const freshTopups = rows
        .filter((tx) => {
          const txId = Number(tx?.transaction_id || 0);
          if (!Number.isFinite(txId) || txId <= prevSeen) return false;
          const refType = String(tx?.ref_type || "").toLowerCase();
          const direction = String(tx?.direction || "").toLowerCase();
          return refType === "topup" && direction === "credit";
        })
        .sort((a, b) => Number(a?.transaction_id || 0) - Number(b?.transaction_id || 0));

      for (const tx of freshTopups) {
        toast.success(t("topup.credited_toast", {
          amount: formatToastAmount(tx?.amount),
          currency: String(tx?.currency || "USDT").toUpperCase(),
        }), {
          id: `topup-credit-${Number(tx?.transaction_id || 0)}`,
        });
      }

      lastSeenBalanceTxIdRef.current = Math.max(prevSeen, maxTxId);
    } catch (err) {
      console.warn("emitTopupCreditToasts failed:", err);
    }
  };

  // Authenticate with backend
  useEffect(() => {
    const authenticate = async () => {
      try {
        const tg = telegram || initTelegramWebApp();
        const oauthQuery = readTelegramOAuthQuery();
        let existingToken = getAuthToken();

        // For plain web version always show auth screen immediately
        // (except OAuth callback when tg_code is present in URL).
        if (!isTelegramWebApp && !oauthQuery.tgCode && !existingToken) {
          setWebAuthRequired(true);
          setAuthError(null);
          setLoading(false);
          return;
        }

        if (oauthQuery.tgError) {
          clearTelegramOAuthQuery();
          const msg = `Telegram login: ${oauthQuery.tgError}`;
          toast.error(msg);
          if (!existingToken) {
            setAuthError(null);
            setWebAuthRequired(true);
            setLoading(false);
            return;
          }
        }

        if (oauthQuery.tgCode) {
          const exchangeRes = await api.post("/auth/telegram-login/exchange", {
            code: oauthQuery.tgCode,
          });
          const exchangedToken = String(exchangeRes?.data?.token || "").trim();
          const exchangedUser = exchangeRes?.data?.user || null;
          const linkedByExchange = Boolean(exchangeRes?.data?.linked);
          await applyAuthenticatedSession(exchangedToken, exchangedUser);
          if (oauthQuery.tgLinked || linkedByExchange) {
            toast.success("Telegram linked");
          } else if (!isTelegramWebApp) {
            navigate("/", { replace: true });
          }
          clearTelegramOAuthQuery();
          return;
        }

        if (oauthQuery.tgLinked && existingToken) {
          toast.success("Telegram linked");
          clearTelegramOAuthQuery();
        }

        // Telegram WebView can reuse the same signed initData after an internal
        // reload. Prefer the existing app session so a harmless reload is not
        // rejected by the backend replay guard.
        if (existingToken) {
          try {
            const meRes = await api.get("/auth/me", {
              headers: { Authorization: `Bearer ${existingToken}` },
            });
            const telegramUserId = Number(tg?.initDataUnsafe?.user?.id || 0);
            const sessionUserId = Number(meRes?.data?.id || 0);
            if (telegramUserId > 0 && sessionUserId !== telegramUserId) {
              setSessionToken("");
              existingToken = "";
            } else {
              await applyAuthenticatedSession(existingToken, meRes.data);
              clearTelegramOAuthQuery();
              return;
            }
          } catch (sessionError) {
            const status = Number(sessionError?.response?.status || 0);
            if (status !== 401 && status !== 403) {
              throw sessionError;
            }
            setSessionToken("");
            existingToken = "";
          }
        }

        let initData = tg?.initData?.trim() || "";
        if (!initData && isTelegramWebApp) {
          initData = await waitForTelegramInitData({ strict: true });
        }

        if (!initData && !existingToken) {
          setWebAuthRequired(true);
          setAuthError(null);
          clearTelegramOAuthQuery();
          return;
        }

        const response = await authenticateTelegram(initData, 3);
        const { user: userData, token } = response.data;
        await applyAuthenticatedSession(token, userData);
        clearTelegramOAuthQuery();
        if (userData?.language_code) {
          const { i18n } = require('react-i18next');
          i18n.changeLanguage(userData.language_code);
        }

      } catch (error) {
        console.error('Auth error:', error);
        const status = Number(error?.response?.status || 0);
        if (status === 401) {
          setSessionToken("");
          clearTelegramOAuthQuery();
          if (!isTelegramWebApp) {
            setAuthError(null);
            setWebAuthRequired(true);
            return;
          }
        }
        const backendDetail = error?.response?.data?.detail;
        const msg = buildAuthErrorMessage(t, backendDetail);
        setAuthError(msg);
        setWebAuthRequired(false);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    if (telegram !== null) {
      authenticate();
      fetchConfig();
    }
  }, [telegram]);

  const fetchWalletData = async (userIdOverride) => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const uid = userIdOverride || user?.id;
      console.log('fetchWalletData called with uid:', uid, 'override:', userIdOverride, 'userState:', user?.id);
      if (!uid) {
        console.warn('fetchWalletData: No uid found, skipping');
        return;
      }

      const [balancesRes, holdsRes, marketRes] = await Promise.allSettled([
        api.get(`/balances/${uid}`),
        api.get(`/topup/holds`),
        api.get(`/market`),
      ]);

      if (balancesRes.status !== "fulfilled") {
        throw balancesRes.reason || new Error("balances_failed");
      }

      const list = balancesRes.value.data?.balances ?? [];
      let holdsList = [];
      let holdsItems = [];
      if (holdsRes.status === "fulfilled") {
        holdsList = holdsRes.value.data?.holds ?? [];
        holdsItems = holdsRes.value.data?.items ?? [];
      } else {
        holdsList = [];
        holdsItems = [];
      }
      const holdsMap = new Map(
        holdsList.map((h) => [String(h.currency).toUpperCase(), Number(h.held_amount) || 0])
      );
      const holdsItemsMap = new Map();
      holdsItems.forEach((item) => {
        const key = String(item.currency).toUpperCase();
        if (!holdsItemsMap.has(key)) holdsItemsMap.set(key, []);
        holdsItemsMap.get(key).push(item);
      });
      // Show spendable balance on home screen: total available (amount - hold), not full amount.
      const totalUsd = list.reduce((sum, row) => {
        const amount = Number(row?.amount ?? 0);
        const hold = Number(row?.hold ?? 0);
        const available = Number(row?.available ?? (amount - hold));
        const usdRate = Number(row?.usd_rate ?? 0);
        if (Number.isFinite(usdRate) && usdRate > 0) {
          return sum + (available * usdRate);
        }
        return sum;
      }, 0);

      const map = new Map(list.map((b) => [String(b.currency).toUpperCase(), b]));
      const newBalances = SUPPORTED_ASSETS.map((asset) => {
        const row = map.get(String(asset.currency).toUpperCase());
        const topupHold = holdsMap.get(String(asset.currency).toUpperCase()) || 0;
        const topupHoldItems = holdsItemsMap.get(String(asset.currency).toUpperCase()) || [];
        const amount = Number(row?.amount ?? 0);
        const hold = Number(row?.hold ?? 0);
        const available = Number(row?.available ?? (amount - hold));
        return {
          ...asset,
          amount,
          hold,
          available,
          usd_value: row?.usd_value ?? null,
          usd_rate: row?.usd_rate ?? null,
          topup_hold: topupHold,
          topup_hold_items: topupHoldItems,
        };
      });

      setWallet(null);
      setBalances(newBalances);
      setTotalBalance(totalUsd);

      // Use the same balance refresh cycle to detect fresh topup credits.
      await emitTopupCreditToasts();

      if (marketRes.status === "fulfilled") {
        setMarket(marketRes.value.data || { prices: {}, changes: {} });
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  const refreshData = async () => {
    await fetchWalletData();
  };

  useEffect(() => {
    lastSeenBalanceTxIdRef.current = 0;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const intervalMs = 5000;
    const tick = () => fetchWalletData();
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    const id = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const token = getAuthToken();
    if (!token || typeof EventSource === 'undefined') return;
    let disposed = false;
    let es = null;

    const handleBalance = () => refreshData();
    const openEventsStream = async () => {
      try {
        const tokenResp = await api.post('/events/token');
        const sseToken = tokenResp?.data?.token;
        if (!sseToken || disposed) return;

        const url = `${BACKEND_URL}/api/events/balance?token=${encodeURIComponent(sseToken)}`;
        if (sseRef.current) {
          try { sseRef.current.close(); } catch (_) { }
        }
        es = new EventSource(url);
        sseRef.current = es;

        es.addEventListener('balance', handleBalance);
        es.addEventListener('init', handleBalance);
        es.onerror = () => {
          // EventSource auto-reconnect is enough while SSE token is valid.
        };
      } catch (e) {
        console.error('Error opening balance events stream:', e);
      }
    };

    openEventsStream();

    return () => {
      disposed = true;
      if (es) {
        es.removeEventListener('balance', handleBalance);
        es.removeEventListener('init', handleBalance);
        try { es.close(); } catch (_) { }
      }
    };
  }, [user?.id, sseReconnectTick]);

  const haptic = (type = 'light') => {
    telegram?.HapticFeedback?.impactOccurred(type);
  };

  // ---------- Код-пароль: замок приложения ----------

  useEffect(() => {
    pinEnabledRef.current = pinEnabled;
  }, [pinEnabled]);
  useEffect(() => {
    pinLockedRef.current = pinLocked;
  }, [pinLocked]);
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const lockNow = () => {
    clearUnlockTicket();
    // Замок поверх шаг-апа выглядел бы как два окна кода друг на друге, а
    // запрос под ним всё равно уже не нужен: человек ушёл из приложения.
    if (stepUpRef.current) {
      stepUpRef.current = null;
      setStepUp(null);
      cancelUnlockWaiters();
    }
    setPinLocked(true);
    pinLockedRef.current = true;
  };

  // Профиль сообщает о включении/выключении пина (после setup-флоу).
  const setPinConfigured = (enabled) => {
    const on = Boolean(enabled);
    setPinEnabled(on);
    pinEnabledRef.current = on;
    if (on) {
      // Код только что придумали — сессия считается разблокированной.
      setHasUnlockedThisSession(true);
    } else {
      setPinLocked(false);
      pinLockedRef.current = false;
      clearUnlockTicket();
      releaseUnlockWaiters();
    }
  };

  const refreshSecurityState = async () => {
    try {
      const res = await api.get("/security/state");
      const on = Boolean(res.data?.pin_enabled);
      setPinEnabled(on);
      pinEnabledRef.current = on;
      return res.data;
    } catch (_) {
      return null;
    }
  };

  const handlePinUnlocked = (ticket, extra = {}) => {
    if (extra?.pinDisabled) {
      setPinEnabled(false);
      pinEnabledRef.current = false;
    }
    setPinLocked(false);
    pinLockedRef.current = false;
    setHasUnlockedThisSession(true);
    // Будим запросы, зависшие на 428 (в т.ч. когда пин сброшен и тикета нет).
    releaseUnlockWaiters();
  };

  const handleLockLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) { /* локальную сессию чистим в любом случае */ }
    try {
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
    } catch (_) { /* ignore */ }
    telegram?.close?.();
    navigate("/");
    window.location.reload();
  };

  // Сигнал из axios-интерцептора: аккаунт заблокирован.
  useEffect(() => onAccountBlocked((detail) => setAccountBlock(detail)), []);

  // Сигнал из axios-интерцептора: денежный запрос упёрся в 428.
  useEffect(() => {
    const off = onLockRequired((reason) => {
      // Шаг-ап показываем листом только там, где у отказа есть внятный исход:
      // страница ловит отмену и просто оставляет всё как было. Для остальных
      // запросов поведение прежнее — полноэкранный замок без кнопки «назад»,
      // иначе отменённый перевод молча превратится в «ничего не произошло».
      if (reason && hasUnlockedThisSession) {
        const next = { reason };
        stepUpRef.current = next;
        setStepUp(next);
        return;
      }
      setPinLocked(true);
      pinLockedRef.current = true;
    });
    return off;
  }, [hasUnlockedThisSession]);

  const closeStepUp = () => {
    stepUpRef.current = null;
    setStepUp(null);
  };

  const handleStepUpUnlocked = (ticket, extra = {}) => {
    closeStepUp();
    handlePinUnlocked(ticket, extra);
  };

  const handleStepUpDismiss = () => {
    closeStepUp();
    cancelUnlockWaiters();
  };

  // Перезапирание при возврате из фона. Слушатели регистрируются один раз,
  // всё состояние — через ref (замыкание на state дало бы устаревшие значения).
  useEffect(() => {
    const markHidden = () => {
      if (hiddenAtRef.current == null) hiddenAtRef.current = Date.now();
    };
    const evaluateReturn = () => {
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null) return;
      if (!pinEnabledRef.current) return;
      if (pinLockedRef.current) return;
      // Нативные оверлеи Telegram (QR-сканер) прячут webview — это не «уход».
      if (hasTrustedOverlay()) return;
      // Страховка по маршрутам оплаты: замок посреди оплаты у кассы недопустим.
      if (/^\/(scan|payment)/.test(String(locationRef.current || ""))) return;
      if (Date.now() - hiddenAt > GRACE_MS) lockNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") markHidden();
      else evaluateReturn();
    };
    const onPageHide = () => markHidden();
    const onPageShow = () => evaluateReturn();
    const onBlur = () => markHidden();
    const onFocus = () => evaluateReturn();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = {
    api,
    user,
    setUser,
    wallet,
    balances,
    totalBalance,
    hideBalance,
    setHideBalance,
    showFiat,
    setShowFiat,
    refreshData,
    telegram,
    syncTelegramTheme, // Added syncTelegramTheme
    isTelegramWebApp,
    setBackHandler,
    clearBackHandler,
    haptic,
    market,
    config,
    preferredCurrency,
    setPreferredCurrency: updatePreferredCurrency,
    loading,
    // Код-пароль
    pinEnabled,
    setPinConfigured,
    refreshSecurityState,
    lockNow
  };

  const orientationGuard = isLandscapeBlocked ? (
    <div className="orientation-lock-overlay" role="alert">
      <p className="orientation-lock-title">{t("common.portrait_only_title")}</p>
      <p className="orientation-lock-description">{t("common.portrait_only_description")}</p>
    </div>
  ) : null;

  // Блокировка перекрывает всё, включая экран загрузки и ошибку авторизации:
  // сервер откажет любому запросу, и показывать поверх этого спиннер или
  // «ошибка авторизации» значит врать о причине.
  if (accountBlock) {
    return (
      <>
        <AccountBlockedScreen message={accountBlock.message} />
        {orientationGuard}
      </>
    );
  }

  // Экран загрузки. Показываем, пока грузимся, И пока не выбрано минимальное
  // время показа: иначе на быстром старте он мигнул бы и исчез.
  if (loading || splashVisible) {
    return (
      <>
        <SplashScreen leaving={splashLeaving} />
        {orientationGuard}
      </>
    );
  }

  if (webAuthRequired) {
    return (
      <>
        <WebAuthScreen
          api={api}
          onAuthSuccess={async (token, userData) => {
            setLoading(true);
            try {
              await applyAuthenticatedSession(token, userData);
              navigate("/", { replace: true });
            } catch (error) {
              const backendDetail = error?.response?.data?.detail;
              const msg = buildAuthErrorMessage(t, backendDetail);
              setAuthError(msg);
              toast.error(msg);
            } finally {
              setLoading(false);
            }
          }}
        />
        {orientationGuard}
      </>
    );
  }

  if (authError) {
    return (
      <>
        <div className="app-container flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 px-6 text-center">
            <p className="text-foreground font-semibold">{t('common.error_auth')}</p>
            <p className="text-muted-foreground text-sm">{authError}</p>
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm"
              onClick={() => window.location.reload()}
            >
              {t('common.try_again')}
            </button>
          </div>
        </div>
        {orientationGuard}
      </>
    );
  }

  // Холодный старт с включённым пином: замок вместо всего дерева,
  // баланс не мигает (pinLocked ставится до fetchWalletData).
  if (pinEnabled && pinLocked && !hasUnlockedThisSession && user) {
    return (
      <>
        <PinLockScreen
          api={api}
          user={user}
          telegram={telegram}
          variant="cold"
          onUnlocked={handlePinUnlocked}
          onLogout={handleLockLogout}
        />
        {orientationGuard}
      </>
    );
  }

  // Hide bottom nav on certain pages.
  //
  // Карты сюда входят целиком: это сценарий с собственной кнопкой назад, и у
  // каждого его экрана снизу закреплено главное действие — выпустить,
  // пополнить. Навигация зафиксирована с z-index 70 поверх всего и такие
  // панели просто накрывает: кнопки не «уезжают», их не видно вовсе, и
  // выглядит это как будто их не сделали. В списке назначений навигации
  // раздела карт нет, так что скрывать её здесь ничего не ломает.
  //
  // Счёт на пополнение криптой (/topup-invoice) — ровно тот же случай, и он
  // приехал сюда с боевого бага: док-панель экрана ~76px, полоса навигации
  // 74px плюс торчащая на 37px вверх кнопка QR — «Получить реквизиты» лежала
  // под ними целиком и тапы забирала навигация. У экрана своя шапка с «Назад»
  // и setBackHandler, так что без полосы он не запирает человека.
  const hideNav = ['/scan', '/payment', '/fortune', '/profile/account', '/cards', '/topup-invoice'].some(
    path => location.pathname.startsWith(path)
  ) || location.pathname.startsWith('/games/');

  return (
    <AppContext.Provider value={contextValue}>
      <div className="app-container">
        {/* Поле света под всем приложением. Смонтировано здесь, а не на
            экранах, по двум причинам: стекло существует только над сценой,
            и при переходах между экранами свет не должен перерисовываться —
            иначе на каждом переходе видна вспышка. */}
        <AmbientField />

        <div className="lp-layer">
        <Routes>
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/scan" element={<QRScannerPage />} />
          <Route path="/payment/:paymentId" element={<PaymentConfirmPage />} />
          <Route path="/topup" element={<TopUpPage />} />
          <Route path="/rub-topup" element={<RubTopUpPage />} />
          <Route path="/topup-invoice" element={<ProcessingTopUpPage />} />
          <Route path="/send" element={<SendPage />} />
          <Route path="/swap" element={<SwapPage />} />
          {/* Backward-compatible alias: some UI buttons still navigate to /exchange */}
          <Route path="/exchange" element={<SwapPage />} />
          <Route path="/history" element={<Layout><HistoryPage /></Layout>} />
          <Route path="/transaction/:txId" element={<Layout><TransactionDetailPage /></Layout>} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/games/magic-cup" element={<MagicCupPage />} />
          <Route path="/games/:gameId" element={<Layout title={t('nav.games')}><GameDetailsPage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/cash-out" element={<CashOutPage />} />
          <Route path="/aml-check" element={<Layout><AmlCheckPage /></Layout>} />
          <Route path="/aml-history" element={<Layout><AmlHistoryPage /></Layout>} />
          <Route path="/cards" element={<Layout><CardsPage /></Layout>} />
          <Route path="/cards/issue" element={<IssueCardPage />} />
          <Route path="/cards/:cardId" element={<CardDetailsPage />} />
          <Route path="/referral" element={<Layout><ReferralPage /></Layout>} />
          <Route path="/fortune" element={<FortuneWheelPage />} />
          <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
          <Route path="/profile/account" element={<Layout><AccountPage /></Layout>} />
        </Routes>
        </div>

        {!hideNav && <BottomNav />}

        <Toaster
          position="top-center"
          toastOptions={{
            // Раньше здесь стоял класс 'glass', которого в проекте не
            // существует, — уведомления просто наследовали стиль sonner.
            className: 'lp-veil lp-veil--edged',
          }}
        />
      </div>
      {/* Шаг-ап: лист поверх экрана, с которого пришёл запрос. Карта, ради
          реквизитов которой спрашивают код, остаётся видна за размытием —
          подтверждают именно её, и вырывать её из кадра незачем. Раньше здесь
          поднимался тот же полноэкранный замок, что и при возврате из фона, и
          читался он как «приложение заперлось», хотя ничего не заперлось. */}
      {stepUp && !pinLocked && (
        <PinLockScreen
          api={api}
          user={user}
          telegram={telegram}
          variant="sheet"
          reason={stepUp.reason}
          onUnlocked={handleStepUpUnlocked}
          onDismiss={handleStepUpDismiss}
        />
      )}
      {/* Перезапирание — оверлеем, НЕ заменой дерева: размонтирование
          QRScannerPage оставило бы активный интент висеть на сервере. */}
      {pinEnabled && pinLocked && hasUnlockedThisSession && (
        <PinLockScreen
          api={api}
          user={user}
          telegram={telegram}
          variant="overlay"
          onUnlocked={handlePinUnlocked}
          onLogout={handleLockLogout}
        />
      )}
      {orientationGuard}
    </AppContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
